import { fetchAuthSession } from 'aws-amplify/auth'

// Each personal service is deployed independently with its own API Gateway,
// so the dashboard routes calls to the right base URL by path prefix.
const FINANCE_API_URL = import.meta.env.VITE_FINANCE_API_URL || ''
const WATCH_API_URL = import.meta.env.VITE_WATCH_API_URL || ''
const FAMILY_API_URL = import.meta.env.VITE_FAMILY_API_URL || ''
const FANTASY_API_URL = import.meta.env.VITE_FANTASY_API_URL || ''
const PLANNER_API_URL = import.meta.env.VITE_PLANNER_API_URL || ''
const ADMIN_API_URL = import.meta.env.VITE_ADMIN_API_URL || FAMILY_API_URL

function baseUrlFor(path: string): string {
  if (path.startsWith('/api/finance')) return FINANCE_API_URL
  if (path.startsWith('/api/watch-monitor')) return WATCH_API_URL
  if (path.startsWith('/api/family')) return FAMILY_API_URL
  if (path.startsWith('/api/fantasy')) return FANTASY_API_URL
  if (path.startsWith('/api/planner')) return PLANNER_API_URL
  if (path.startsWith('/api/admin')) return ADMIN_API_URL
  return ''
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  try {
    const session = await fetchAuthSession()
    const token = session.tokens?.idToken?.toString()
    if (token) {
      return {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      }
    }
  } catch {
    // Fall through to default headers
  }
  return { 'Content-Type': 'application/json' }
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

/**
 * Every service answers a failure with `{"error": "<user-presentable reason>"}`
 * — SimpleFIN claim rejections, missing access grants, validation failures. A
 * bare "API error: 400" throws that reason away and sends the owner digging
 * through CloudWatch for a message the response already carried, so surface the
 * body and keep the status code on the error for callers that branch on it.
 */
async function throwIfNotOk(response: Response): Promise<void> {
  if (response.ok) return
  let detail = ''
  try {
    const text = (await response.text()).trim()
    if (text) {
      try {
        const parsed = JSON.parse(text) as { error?: string; message?: string }
        detail = parsed.error || parsed.message || text
      } catch {
        // Not JSON — an API Gateway or proxy error page. Keep it short.
        detail = text
      }
    }
  } catch {
    // Body unreadable; fall back to the status line below.
  }
  if (detail.length > 300) detail = `${detail.slice(0, 300)}…`
  throw new ApiError(
    detail || `API error: ${response.status} ${response.statusText}`,
    response.status,
  )
}

export async function apiGet<T>(path: string): Promise<T> {
  const headers = await getAuthHeaders()
  const response = await fetch(`${baseUrlFor(path)}${path}`, { headers })
  await throwIfNotOk(response)
  return response.json()
}

export async function apiPut<T>(path: string, body: unknown): Promise<T> {
  const headers = await getAuthHeaders()
  const response = await fetch(`${baseUrlFor(path)}${path}`, {
    method: 'PUT',
    headers,
    body: JSON.stringify(body),
  })
  await throwIfNotOk(response)
  return response.json()
}

export async function apiDelete<T>(path: string): Promise<T> {
  const headers = await getAuthHeaders()
  const response = await fetch(`${baseUrlFor(path)}${path}`, {
    method: 'DELETE',
    headers,
  })
  await throwIfNotOk(response)
  return response.json()
}

/**
 * Multipart upload. The browser must set its own Content-Type so the multipart
 * boundary is included, so the JSON content type is stripped from the headers.
 */
export async function apiUpload<T>(path: string, form: FormData): Promise<T> {
  const headers = await getAuthHeaders()
  delete headers['Content-Type']
  const response = await fetch(`${baseUrlFor(path)}${path}`, {
    method: 'POST',
    headers,
    body: form,
  })
  await throwIfNotOk(response)
  return response.json()
}

export async function apiPost<T>(path: string, body?: unknown): Promise<T> {
  const headers = await getAuthHeaders()
  const response = await fetch(`${baseUrlFor(path)}${path}`, {
    method: 'POST',
    headers,
    body: body ? JSON.stringify(body) : undefined,
  })
  await throwIfNotOk(response)
  return response.json()
}
